import { secureAuthUtils } from './secureAuth.js';

class ApiClient {
  constructor() {
    // Point to the 2W billing backend admin API
    // Vite uses import.meta.env for environment variables
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/admin';
    
    // Request deduplication cache
    this.pendingRequests = new Map();
    
    // Token will be loaded on first request (lazy loading)
    this.token = null;
    this.tokenPromise = null;
  }

  async ensureToken() {
    if (this.token) {
      // Validate existing token is still valid
      try {
        // Check if token is expired or malformed
        if (typeof this.token !== 'string' || this.token.length < 10) {
          this.token = null;
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('ApiClient: Invalid token format, clearing:', error);
        }
        this.token = null;
      }
    }
    
    if (!this.token && !this.tokenPromise) {
      this.tokenPromise = (async () => {
        try {
          const token = await secureAuthUtils.getToken();
          if (!token) {
            // Token doesn't exist or is expired - this is expected, not an error
            return null;
          }
          if (typeof token !== 'string') {
            throw new Error('Invalid token format received from secure auth');
          }
          return token;
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('ApiClient: Failed to get token:', error);
          }
          this.tokenPromise = null;
          throw error;
        }
      })();
    }
    
    if (this.tokenPromise) {
      this.token = await this.tokenPromise;
      this.tokenPromise = null;
    }
    
    // Don't throw error for missing token - some requests (like login) don't need authentication
    return this.token;
  }

  async setToken(token, userData) {
    this.token = token;
    // Use secure storage instead of localStorage
    await secureAuthUtils.setToken(token, userData);
  }

  async clearToken() {
    this.token = null;
    // Clear secure session
    secureAuthUtils.clearSession();
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const maxRetries = options.retry === false ? 1 : 3;
    const retryDelay = options.retryDelay || 1000;
    const deduplicationKey = this.getDeduplicationKey(endpoint, options);
    let lastError;

    // Request deduplication for GET requests (only if not explicitly disabled)
    if ((!options.method || options.method === 'GET') && options.deduplicate !== false) {
      const existingRequest = this.pendingRequests.get(deduplicationKey);
      if (existingRequest) {
        return existingRequest;
      }
    }

    // Create or use existing AbortController
    let abortController = options.abortController;
    
    if (!abortController) {
      abortController = new AbortController();
      
      // If external signal provided, link it to our controller
      if (options.signal) {
        options.signal.addEventListener('abort', () => {
          abortController.abort();
        });
      }
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Ensure we have the latest token from secure storage
        await this.ensureToken();

        // Update activity timestamp for authenticated requests
        if (this.token) {
          secureAuthUtils.updateActivity();
        }

        const config = {
          headers: {
            'Content-Type': 'application/json',
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
            ...options.headers,
          },
          signal: abortController?.signal || options.signal,
          ...options,
        };

        // Add CSRF protection for non-GET requests
        if (options.method && options.method !== 'GET') {
          const csrfToken = secureAuthUtils.getCSRFToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        // Remove internal options before passing to fetch
        delete config.retry;
        delete config.retryDelay;
        delete config.deduplicate;
        delete config.abortController;

        const requestPromise = fetch(url, config);
        
        // Store promise for deduplication (GET requests only)
        if ((!options.method || options.method === 'GET') && options.deduplicate !== false) {
          this.pendingRequests.set(deduplicationKey, requestPromise);
        }

        const response = await requestPromise;
        
        // Handle 401 - enhanced authentication error handling
        if (response.status === 401) {
          const authError = response.headers.get('X-Auth-Error');
          
          // Only clear token on actual invalid token, not network issues
          if (authError === 'invalid_token' || authError === 'token_expired') {
            console.warn('Authentication failed, clearing secure session');
            await this.clearToken();
            
            // Emit authentication failure event
            window.dispatchEvent(new CustomEvent('auth_failure', {
              detail: { reason: authError, endpoint: endpoint }
            }));
            
            return Promise.reject(new Error('Authentication failed'));
          }
          
          // For other 401s (e.g., temporary auth service issues), retry
          if (attempt < maxRetries) {
            lastError = new Error('Authentication temporarily unavailable');
            await this.delay(retryDelay * Math.pow(2, attempt - 1));
            continue;
          }
        }

        // Handle other error responses
        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Network error' }));
          const errorMessage = error.error || error.message || 'Request failed';
          
          // Don't retry client errors (400-499) except 401, 429
          if (response.status >= 400 && response.status < 500 && 
              response.status !== 401 && response.status !== 429) {
            throw new Error(errorMessage);
          }
          
          // Retry server errors and rate limiting
          if (attempt < maxRetries) {
            lastError = new Error(errorMessage);
            
            // Handle rate limiting with longer delay
            if (response.status === 429) {
              const retryAfter = response.headers.get('Retry-After');
              const delay = retryAfter ? parseInt(retryAfter) * 1000 : retryDelay * 4;
              await this.delay(delay);
            } else {
              // Exponential backoff with jitter for other errors
              const delay = Math.min(
                retryDelay * Math.pow(2, attempt - 1) + Math.random() * 1000,
                10000
              );
              await this.delay(delay);
            }
            continue;
          }
          
          throw new Error(errorMessage);
        }

        // Cleanup deduplication cache on successful response
        if ((!options.method || options.method === 'GET') && options.deduplicate !== false) {
          this.pendingRequests.delete(deduplicationKey);
        }

        return response.json();
      } catch (error) {
        lastError = error;
        
        // Cleanup deduplication cache on error
        if ((!options.method || options.method === 'GET') && options.deduplicate !== false) {
          this.pendingRequests.delete(deduplicationKey);
        }
        
        // Handle AbortError - don't retry aborted requests
        if (error.name === 'AbortError') {
          throw new Error('Request was cancelled');
        }
        
        // Network errors - always retry
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          if (attempt < maxRetries) {
            console.warn(`Network error on attempt ${attempt}/${maxRetries}, retrying...`);
            await this.delay(retryDelay * Math.pow(2, attempt - 1));
            continue;
          }
          throw new Error('Unable to connect to 2W billing backend. Please check if the service is running.');
        }
        
        // Don't retry non-network errors on last attempt
        if (attempt === maxRetries) {
          throw error;
        }
      }
    }
    
    throw lastError || new Error('Request failed after retries');
  }

  // Helper method for delays
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Generate deduplication key for requests with proper collision prevention
  getDeduplicationKey(endpoint, options) {
    const method = options.method || 'GET';
    const body = options.body || '';
    const queryParams = endpoint.includes('?') ? endpoint.split('?')[1] : '';
    
    // Use proper delimiter and encoding to prevent collisions
    const components = [
      method,
      encodeURIComponent(endpoint),
      encodeURIComponent(queryParams),
      encodeURIComponent(body)
    ];
    
    return components.join('||');
  }

  // Clear all pending requests (useful for cleanup)
  clearPendingRequests() {
    this.pendingRequests.clear();
  }

  // Admin authentication with enhanced security
  async login(email, password) {
    // Generate CSRF token for session
    const csrfToken = secureAuthUtils.generateCSRFToken();
    
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        password,
        csrfToken // Include CSRF token in login request
      }),
    });
    
    if (response.token) {
      // Use secure token storage
      await this.setToken(response.token, response.user);
      
      // Emit successful login event
      window.dispatchEvent(new CustomEvent('auth_success', {
        detail: { user: response.user }
      }));
    }
    
    return response;
  }

  // Brand management
  async getBrands(params = {}) {
    const { signal, ...queryParams } = params;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null && value !== '')
    ).toString();
    const result = await this.request(`/brands${queryString ? `?${queryString}` : ''}`, { signal });
    return result;
  }

  async getBrand(id) {
    return this.request(`/brands/${id}`);
  }

  async updateBrand(id, data) {
    return this.request(`/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Commission overrides
  async createCommissionOverride(brandId, data) {
    return this.request(`/brands/${brandId}/commission-overrides`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCommissionOverride(brandId, overrideId) {
    return this.request(`/brands/${brandId}/commission-overrides/${overrideId}`, {
      method: 'DELETE',
    });
  }

  // Enhanced Shipping fees - Updated for new weight range system
  async createShippingFee(brandId, data) {
    const apiData = {
      shippingMethod: data.shippingMethod,
      weightMin: data.weightMin !== null && data.weightMin !== '' ? parseFloat(data.weightMin) : null,
      weightMax: data.weightMax !== null && data.weightMax !== '' ? parseFloat(data.weightMax) : null,
      fee: parseFloat(data.fee),
      returnFee: data.returnFee && data.returnFee !== '' ? parseFloat(data.returnFee) : null
    };

    return this.request(`/brands/${brandId}/shipping-fees`, {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  async updateShippingFee(brandId, feeId, data) {
    const apiData = {
      shippingMethod: data.shippingMethod,
      weightMin: data.weightMin !== null && data.weightMin !== '' ? parseFloat(data.weightMin) : null,
      weightMax: data.weightMax !== null && data.weightMax !== '' ? parseFloat(data.weightMax) : null,
      fee: parseFloat(data.fee),
      returnFee: data.returnFee && data.returnFee !== '' ? parseFloat(data.returnFee) : null
    };

    return this.request(`/brands/${brandId}/shipping-fees/${feeId}`, {
      method: 'PUT',
      body: JSON.stringify(apiData),
    });
  }

  async validateShippingFeeRule(brandId, data) {
    const apiData = {
      shippingMethod: data.shippingMethod,
      weightMin: data.weightMin !== null && data.weightMin !== '' ? parseFloat(data.weightMin) : null,
      weightMax: data.weightMax !== null && data.weightMax !== '' ? parseFloat(data.weightMax) : null,
      fee: parseFloat(data.fee),
      returnFee: data.returnFee && data.returnFee !== '' ? parseFloat(data.returnFee) : null,
      excludeRuleId: data.excludeRuleId || null
    };

    return this.request(`/brands/${brandId}/shipping-fees/validate`, {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  // getShippingRules removed - ShippingRule system deprecated in favor of weight-based ShippingFeeTable

  async deleteShippingFee(brandId, feeId) {
    return this.request(`/brands/${brandId}/shipping-fees/${feeId}`, {
      method: 'DELETE',
    });
  }

  async resetShippingFeesToDefault(brandId) {
    return this.request(`/brands/${brandId}/shipping-fees/reset`, {
      method: 'DELETE',
    });
  }

  // Return fee management
  async getDefaultReturnFee(brandId) {
    return this.request(`/brands/${brandId}/return-fees`);
  }

  async updateDefaultReturnFee(brandId, returnFee) {
    return this.request(`/brands/${brandId}/return-fees`, {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async deleteDefaultReturnFee(brandId) {
    return this.request(`/brands/${brandId}/return-fees`, {
      method: 'DELETE',
    });
  }

  async getMarketplaceReturnFee(brandId, marketplace) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`);
  }

  async createMarketplaceReturnFee(brandId, marketplace, returnFee) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'POST',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async updateMarketplaceReturnFee(brandId, marketplace, returnFee) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async deleteMarketplaceReturnFee(brandId, marketplace) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'DELETE',
    });
  }

  async resetMarketplaceReturnFeesToDefault(brandId) {
    return this.request(`/brands/${brandId}/return-fees/marketplace/reset-all`, {
      method: 'DELETE',
    });
  }

  // Global return fee defaults management
  async getGlobalReturnFeeDefaults() {
    return this.request('/return-fees/defaults');
  }

  async updateGlobalReturnFeeDefault(returnFee) {
    return this.request('/return-fees/defaults', {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async createGlobalMarketplaceReturnFee(marketplace, returnFee) {
    return this.request(`/return-fees/defaults/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'POST',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async updateGlobalMarketplaceReturnFee(marketplace, returnFee) {
    return this.request(`/return-fees/defaults/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'PUT',
      body: JSON.stringify({ returnFee: parseFloat(returnFee) }),
    });
  }

  async deleteGlobalMarketplaceReturnFee(marketplace) {
    return this.request(`/return-fees/defaults/marketplace/${encodeURIComponent(marketplace)}`, {
      method: 'DELETE',
    });
  }

  // Sync coverage tracking
  async getSyncCoverage(brandId) {
    return this.request(`/orders/${brandId}/sync/coverage`);
  }

  // Activity logging
  async getBrandActivity(brandId) {
    try {
      return this.request(`/brands/${brandId}/activity`);
    } catch (error) {
      // If activity endpoint doesn't exist yet, return empty array
      console.warn('Activity logging not implemented yet:', error.message);
      return [];
    }
  }

  // Brand-specific analytics
  async getBrandAnalytics(brandId, params = {}) {
    const queryParams = {
      brand: brandId,
      ...params
    };
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/detailed?${queryString}`);
  }

  // ==========================================
  // ANALYTICS ENDPOINTS - Enhanced for Admin Dashboard
  // ==========================================

  /**
   * Get system-wide analytics overview
   * @returns {Promise<Object>} Overview metrics across all brands
   */
  async getAnalyticsOverview(options = {}) {
    const { signal } = options;
    return this.request('/analytics/overview', { signal });
  }

  /**
   * Get detailed analytics with filtering options
   * @param {Object} params - Filter parameters
   * @param {string} [params.brand] - Brand ID or 'all'
   * @param {number} [params.days=30] - Number of days to analyze
   * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
   * @param {string} [params.endDate] - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Detailed analytics data
   */
  async getDetailedAnalytics(params = {}) {
    const { signal, ...queryParams } = params;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/detailed?${queryString}`, { signal });
  }

  /**
   * Get brand-specific performance reports
   * @param {Object} params - Filter parameters
   * @param {string} [params.brand] - Brand ID or 'all'
   * @param {number} [params.days=30] - Number of days to analyze
   * @returns {Promise<Array>} Array of brand performance data
   */
  async getBrandReports(params = {}) {
    const { signal, ...queryParams } = params;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/brand-reports?${queryString}`, { signal });
  }

  /**
   * Get marketplace analytics and breakdown
   * @param {Object} params - Filter parameters
   * @param {string} [params.brand] - Brand ID or 'all'
   * @param {number} [params.days=30] - Number of days to analyze
   * @returns {Promise<Array>} Marketplace performance data
   */
  async getMarketplaceAnalytics(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/analytics/marketplace?${queryString}`);
  }

  /**
   * Get cross-brand commission summary
   * @param {Object} params - Filter parameters
   * @returns {Promise<Object>} Commission summary across all brands
   */
  async getCommissionSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/commission-summary?${queryString}`);
  }

  /**
   * Get trending data for dashboard charts
   * @param {Object} params - Parameters
   * @param {string} params.metric - Metric to track (revenue, commission, orders)
   * @param {string} params.period - Time period (week, month, quarter, year)
   * @returns {Promise<Array>} Trending data points
   */
  async getTrendingData(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/analytics/trends?${queryString}`);
  }

  // Billing operations
  async getBillingReport(brandId, year, month, options = {}) {
    const queryString = new URLSearchParams(options).toString();
    return this.request(`/billing/${brandId}/${year}/${month}?${queryString}`);
  }

  async generateBillingReport(brandId, year, month) {
    const response = await this.request(`/billing/${brandId}/${year}/${month}/generate`, {
      method: 'POST',
    });

    // If we got a cached report (200), return it directly
    if (response.cached || response.id) {
      return response;
    }

    // If async generation started (202), poll for completion
    if (response.status === 'generating' && response.jobId) {
      return this.pollBillingJobStatus(response.jobId, brandId, year, month);
    }

    return response;
  }

  async getBillingJobStatus(jobId) {
    return this.request(`/billing/job/${jobId}/status`);
  }

  async pollBillingJobStatus(jobId, brandId, year, month, maxAttempts = 120, intervalMs = 2000) {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const status = await this.getBillingJobStatus(jobId);

      if (status.status === 'completed') {
        // Return the report from the job or fetch it
        if (status.report) {
          return status.report;
        }
        // Fetch the latest report
        return this.getBillingReport(brandId, year, month);
      }

      if (status.status === 'failed') {
        throw new Error(status.failedReason || 'Report generation failed');
      }

      if (status.status === 'not_found') {
        throw new Error('Job not found');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    }

    throw new Error('Report generation timed out. Please try again.');
  }

  // ==========================================
  // ORDER MANAGEMENT - Enhanced for Admin Dashboard
  // ==========================================

  /**
   * Get orders for a specific brand with pagination and filtering
   * @param {string} brandId - Brand ID
   * @param {Object} options - Query options
   * @param {number} [options.page=1] - Page number
   * @param {number} [options.limit=50] - Items per page
   * @param {string} [options.search] - Search term
   * @param {string} [options.sortBy] - Sort field
   * @param {string} [options.sortDirection] - Sort direction (asc/desc)
   * @param {Array} [options.statusFilter] - Status filters
   * @param {string} [options.startDate] - Start date filter
   * @param {string} [options.endDate] - End date filter
   * @returns {Promise<Object>} Paginated orders data
   */
  async getBrandOrders(brandId, options = {}) {
    const queryString = new URLSearchParams(
      Object.entries(options).filter(([, value]) => value != null)
    ).toString();
    const url = `/orders/${brandId}?${queryString}`;
    return this.request(url);
  }

  /**
   * Get list of all distinct marketplaces for filter dropdowns
   * @param {Object} options - Query options
   * @param {string} [options.brandId] - Optional brand ID to filter marketplaces
   * @returns {Promise<Object>} List of marketplaces
   */
  async getMarketplacesList(options = {}) {
    try {
      const { signal, brandId, ...queryParams } = options;
      const params = { ...queryParams };
      if (brandId && brandId !== 'all') {
        params.brandId = brandId;
      }
      const queryString = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value != null)
      ).toString();
      const url = `/marketplaces/list${queryString ? `?${queryString}` : ''}`;
      return this.request(url, { signal });
    } catch (error) {
      // Return empty list on error
      console.error('Failed to fetch marketplaces list:', error);
      return { marketplaces: [], count: 0 };
    }
  }

  /**
   * Get orders across all brands (admin view)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Cross-brand orders data
   */
  async getAllBrandOrders(options = {}) {
    try {
      const { signal, ...queryParams } = options;
      const queryString = new URLSearchParams(
        Object.entries(queryParams).filter(([, value]) => value != null)
      ).toString();
      const url = `/orders?${queryString}`;
      return this.request(url, { signal });
    } catch (error) {
      // Don't return fallback data for cancelled requests
      if (error?.message === 'Request was cancelled') {
        throw error;
      }
      console.error('Error fetching all brand orders:', error);
      return { orders: [], pagination: { page: 1, limit: options.limit || 50, totalOrders: 0, totalPages: 0 } };
    }
  }

  /**
   * Get order details by ID
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Order details
   */
  async getOrderDetails(orderId) {
    return this.request(`/orders/details/${orderId}`);
  }

  /**
   * Sync orders for a brand
   * @param {string} brandId - Brand ID
   * @param {Object} options - Sync options
   * @param {string} [options.startDate] - Start date for historical sync (YYYY-MM-DD)
   * @param {string} [options.endDate] - End date for historical sync (YYYY-MM-DD)
   * @param {boolean} [options.forceSync] - Force sync even if one is already running
   * @param {number} [options.maxMonths] - Maximum months to sync (default: 60)
   * @returns {Promise<Object>} Sync job details
   */
  async syncOrders(brandId, options = {}) {
    return this.request(`/orders/${brandId}/sync`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  /**
   * Get sync status for a brand
   * @param {string} brandId - Brand ID
   * @returns {Promise<Object>} Sync status details
   */
  async getSyncStatus(brandId) {
    return this.request(`/orders/${brandId}/sync/status`);
  }


  /**
   * Get marketplace breakdown for orders
   * @param {Object} params - Parameters
   * @param {string} [params.brandId] - Specific brand or 'all'
   * @param {string} [params.period] - Time period
   * @returns {Promise<Array>} Marketplace breakdown data
   */
  async getOrderMarketplaceBreakdown(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/orders/marketplace-breakdown?${queryString}`);
  }


  // User management (for future implementation)
  async getUsers() {
    return this.request('/users');
  }

  async createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // ==========================================
  // MARKETPLACE PATTERNS API - Step 1.2 Implementation
  // ==========================================

  /**
   * Get marketplace patterns with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=50] - Items per page
   * @param {string} [params.marketplace] - Filter by marketplace name
   * @param {string} [params.isActive] - Filter by active status
   * @param {string} [params.search] - Search term
   * @returns {Promise<Object>} Paginated patterns data
   */
  async getMarketplacePatterns(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null && value !== '')
    ).toString();
    return this.request(`/marketplace-patterns${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Create a new marketplace pattern
   * @param {Object} patternData - Pattern configuration
   * @param {string} patternData.marketplaceName - Marketplace name
   * @param {string} [patternData.displayName] - Optional display name
   * @param {string} patternData.patternType - Pattern type (EXACT, CONTAINS, etc.)
   * @param {string} patternData.patternValue - Pattern value/expression
   * @param {number} [patternData.priority=1] - Priority (1-10)
   * @param {boolean} [patternData.isActive=true] - Active status
   * @param {boolean} [patternData.isCaseSensitive=false] - Case sensitivity
   * @param {string} [patternData.category='primary'] - Pattern category
   * @param {string} [patternData.description] - Description/notes
   * @returns {Promise<Object>} Created pattern
   */
  async createMarketplacePattern(patternData) {
    return this.request('/marketplace-patterns', {
      method: 'POST',
      body: JSON.stringify(patternData)
    });
  }

  /**
   * Update an existing marketplace pattern
   * @param {string} id - Pattern ID
   * @param {Object} patternData - Updated pattern data
   * @returns {Promise<Object>} Updated pattern
   */
  async updateMarketplacePattern(id, patternData) {
    return this.request(`/marketplace-patterns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patternData)
    });
  }

  /**
   * Delete a marketplace pattern
   * @param {string} id - Pattern ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteMarketplacePattern(id) {
    return this.request(`/marketplace-patterns/${id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Test a marketplace pattern against sample tags
   * @param {Object} testData - Test configuration
   * @param {string} testData.patternValue - Pattern to test
   * @param {string} testData.patternType - Pattern type
   * @param {Array<string>} [testData.testTags] - Tags to test against
   * @returns {Promise<Object>} Test results with validation and matches
   */
  async testMarketplacePattern(testData) {
    return this.request('/marketplace-patterns/test', {
      method: 'POST',
      body: JSON.stringify(testData)
    });
  }

  /**
   * Get marketplace pattern usage analytics
   * @param {Object} params - Analytics parameters
   * @param {string} [params.brandId] - Specific brand or 'all'
   * @param {number} [params.days=30] - Analysis period in days
   * @returns {Promise<Object>} Analytics data with usage stats and distribution
   */
  async getMarketplacePatternsAnalytics(params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null)
    ).toString();
    return this.request(`/marketplace-patterns/analytics${queryString ? `?${queryString}` : ''}`);
  }

  // ==========================================
  // EXPORT & UTILITY METHODS
  // ==========================================


  /**
   * Check API health and connectivity
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      // Simple health check that doesn't require auth
      const response = await fetch(`${this.baseUrl.replace('/admin', '')}/health`);
      return { status: 'ok', timestamp: new Date().toISOString() };
    } catch (error) {
      return { status: 'error', error: error.message, timestamp: new Date().toISOString() };
    }
  }

  /**
   * Get current admin user profile
   * @returns {Promise<Object>} User profile data
   */
  async getCurrentUser() {
    return this.request('/auth/profile');
  }

  /**
   * Refresh authentication token with secure handling
   * @returns {Promise<Object>} New token data
   */
  async refreshToken() {
    try {
      const currentToken = await secureAuthUtils.getToken();
      if (!currentToken) {
        throw new Error('No token available for refresh');
      }

      const response = await this.request('/auth/refresh', {
        method: 'POST',
        retry: false, // Don't retry token refresh to avoid loops
      });

      if (response.token) {
        // Update secure storage with new token
        const userData = await secureAuthUtils.getUserData();
        await this.setToken(response.token, userData);
        
        console.log('Token refreshed successfully');
        
        // Emit token refresh event
        window.dispatchEvent(new CustomEvent('token_refreshed', {
          detail: { timestamp: Date.now() }
        }));
      }

      return response;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      // If refresh fails, clear session and logout
      await this.clearToken();
      window.dispatchEvent(new CustomEvent('auth_failure', {
        detail: { reason: 'refresh_failed', error: error.message }
      }));
      
      throw error;
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Format currency for display
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default: USD)
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  }

  /**
   * Format percentage for display
   * @param {number} rate - Rate as decimal (e.g., 0.30)
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted percentage string
   */
  formatPercentage(rate, decimals = 1) {
    return `${((rate || 0) * 100).toFixed(decimals)}%`;
  }

  /**
   * Format weight for display (pounds with kg conversion)
   * @param {number} pounds - Weight in pounds
   * @returns {string} Formatted weight string (e.g., "1.5 lb (0.68 kg)")
   */
  formatWeight(pounds) {
    if (!pounds || pounds === 0) return '0 lb (0 kg)';
    const kg = (pounds * 0.453592).toFixed(2);
    return `${pounds.toFixed(2)} lb (${kg} kg)`;
  }

  /**
   * Format date for display
   * @param {string|Date} date - Date to format
   * @param {string} format - Format type ('short', 'long', 'time')
   * @returns {string} Formatted date string
   */
  formatDate(date, format = 'short') {
    if (!date) return '';
    
    const dateObj = new Date(date);
    const options = {
      short: { year: 'numeric', month: 'short', day: 'numeric' },
      long: { year: 'numeric', month: 'long', day: 'numeric' },
      time: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
    };
    
    return dateObj.toLocaleDateString('en-US', options[format] || options.short);
  }

  /**
   * Generate query key for React Query caching
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Array} Query key array
   */
  getQueryKey(endpoint, params = {}) {
    return [endpoint, params];
  }

  /**
   * Calculate percentage change between two values
   * @param {number} current - Current value
   * @param {number} previous - Previous value
   * @returns {Object} Change data with percentage and direction
   */
  calculateChange(current, previous) {
    if (!previous || previous === 0) {
      return { percentage: current > 0 ? 100 : 0, direction: 'up', isNew: true };
    }

    const change = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(change),
      direction: change >= 0 ? 'up' : 'down',
      isNew: false,
    };
  }

  // ==========================================
  // COMMISSION ADJUSTMENT METHODS
  // ==========================================

  /**
   * Get commission adjustments with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getCommissionAdjustments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/commission-adjustments${queryString ? `?${queryString}` : ''}`;

    return this.request(endpoint, {
      signal: params.signal,
    });
  }

  /**
   * Get pending commission adjustments for approval
   * @param {Object} options - Request options
   * @returns {Promise} API response
   */
  async getPendingAdjustments(options = {}) {
    return this.request('/commission-adjustments/pending', {
      signal: options.signal,
    });
  }

  /**
   * Create a new commission adjustment
   * @param {Object} adjustmentData - Adjustment data
   * @returns {Promise} API response
   */
  async createCommissionAdjustment(adjustmentData) {
    return this.request('/commission-adjustments', {
      method: 'POST',
      body: JSON.stringify(adjustmentData),
    });
  }

  /**
   * Update an existing commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @param {Object} updateData - Update data
   * @returns {Promise} API response
   */
  async updateCommissionAdjustment(adjustmentId, updateData) {
    return this.request(`/commission-adjustments/${adjustmentId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  /**
   * Approve a commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @returns {Promise} API response
   */
  async approveCommissionAdjustment(adjustmentId) {
    return this.request(`/commission-adjustments/${adjustmentId}/approve`, {
      method: 'POST',
    });
  }

  /**
   * Reject a commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} API response
   */
  async rejectCommissionAdjustment(adjustmentId, reason = '') {
    return this.request(`/commission-adjustments/${adjustmentId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Delete a commission adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @param {string} reason - Deletion reason
   * @returns {Promise} API response
   */
  async deleteCommissionAdjustment(adjustmentId, reason = '') {
    return this.request(`/commission-adjustments/${adjustmentId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Get audit trail for an adjustment
   * @param {string} adjustmentId - Adjustment ID
   * @returns {Promise} API response
   */
  async getAdjustmentAuditTrail(adjustmentId) {
    return this.request(`/commission-adjustments/${adjustmentId}/audit`);
  }

  /**
   * Calculate adjustment impact before creating
   * @param {Object} impactData - Impact calculation data
   * @returns {Promise} API response
   */
  async calculateAdjustmentImpact(impactData) {
    return this.request('/commission-adjustments/calculate-impact', {
      method: 'POST',
      body: JSON.stringify(impactData),
    });
  }

  // ============================================
  // INVOICE MANAGEMENT METHODS
  // ============================================

  /**
   * Get invoice settings for a brand
   * @param {string} brandId - Brand ID
   * @returns {Promise} API response
   */
  async getInvoiceSettings(brandId) {
    return this.request(`/brands/${brandId}/invoice-settings`);
  }

  /**
   * Update invoice settings for a brand
   * @param {string} brandId - Brand ID
   * @param {Object} settings - Invoice settings
   * @returns {Promise} API response
   */
  async updateInvoiceSettings(brandId, settings) {
    return this.request(`/brands/${brandId}/invoice-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  /**
   * Get all invoices with filters
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getInvoices(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoices${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get single invoice by ID
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async getInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}`);
  }

  /**
   * Trigger monthly invoice generation for all configured brands
   * Uses optimized path that reads from BillingReport (no recalculation)
   * @param {Object} data - { monthString: 'YYYY-MM' } (optional, defaults to previous month)
   * @returns {Promise} API response
   */
  async triggerMonthlyInvoices(data = {}) {
    return this.request('/invoices/trigger-monthly', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Generate invoice from billing report (optimized - single brand)
   * @param {Object} data - { brandId, monthString }
   * @returns {Promise} API response
   */
  async generateInvoiceFromReport(data) {
    return this.request('/invoices/generate-from-report', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Approve invoice
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async approveInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}/approve`, {
      method: 'PUT',
    });
  }

  /**
   * Send invoice email
   * @param {string} invoiceId - Invoice ID
   * @param {string[]} recipients - Email recipients (optional)
   * @returns {Promise} API response
   */
  async sendInvoice(invoiceId, recipients = null) {
    return this.request(`/invoices/${invoiceId}/send`, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    });
  }

  /**
   * Record payment for invoice
   * @param {string} invoiceId - Invoice ID
   * @param {Object} paymentData - Payment details
   * @returns {Promise} API response
   */
  async recordInvoicePayment(invoiceId, paymentData) {
    return this.request(`/invoices/${invoiceId}/payment`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  /**
   * Cancel invoice
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async cancelInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}/cancel`, {
      method: 'PUT',
    });
  }

  /**
   * Delete invoice (only DRAFT or CANCELLED invoices)
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async deleteInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Regenerate invoice (only DRAFT invoices)
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response with updated invoice
   */
  async regenerateInvoice(invoiceId) {
    return this.request(`/invoices/${invoiceId}/regenerate`, {
      method: 'POST',
    });
  }

  /**
   * Get invoice PDF download URL
   * @param {string} invoiceId - Invoice ID
   * @returns {string} PDF download URL
   */
  getInvoicePdfUrl(invoiceId) {
    return `${this.baseUrl}/invoices/${invoiceId}/pdf`;
  }

  /**
   * Download invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} Blob response
   */
  async downloadInvoicePdf(invoiceId) {
    await this.ensureToken();
    const response = await fetch(`${this.baseUrl}/invoices/${invoiceId}/pdf`, {
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    return response.blob();
  }

  /**
   * Regenerate invoice PDF
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} API response
   */
  async regenerateInvoicePdf(invoiceId) {
    return this.request(`/invoices/${invoiceId}/regenerate-pdf`, {
      method: 'POST',
    });
  }

  /**
   * Verify email configuration
   * @returns {Promise} API response
   */
  async verifyEmailConfig() {
    return this.request('/email/verify');
  }

  /**
   * Send test email
   * @param {string} to - Email address
   * @returns {Promise} API response
   */
  async sendTestEmail(to) {
    return this.request('/email/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    });
  }
}

export const apiClient = new ApiClient();
export default apiClient;